import React, { useState, useEffect } from 'react';
import { HIDDEN_ROLES } from 'constants/roles';
import { togglesObj } from '../../utils/crud';
import Permission from '../Permission';
import CrudForm from '../CrudForm';
import ToggleForm from '../ToggleForm';
import PeoplePermissionsSection from './PeoplePermissionsSection';

// Helper for per-dialog skip logic
const DONT_SHOW_KEY_PREFIX = 'dontShowDialog_';
function shouldSkipDialog(dialogKey) {
  const data = JSON.parse(
    localStorage.getItem(DONT_SHOW_KEY_PREFIX + dialogKey) || 'null',
  );
  if (!data) return false;
  const now = Date.now();
  return now - data.timestamp < 86400000; // 24 hours
}
function setSkipDialog(dialogKey) {
  localStorage.setItem(
    DONT_SHOW_KEY_PREFIX + dialogKey,
    JSON.stringify({ timestamp: Date.now() }),
  );
}

// Unique keys for enable/disable dialogs
const PEOPLE_DIALOG_KEY_ENABLE = 'people_permission_dialog_enable';
const PEOPLE_DIALOG_KEY_DISABLE = 'people_permission_dialog_disable';

const Form = ({
  macros,
  updateEditPermissions,
  updateRoleRequestBody,
  roleRequestBody,
}) => {
  const getInitialPeopleToggles = () => {
    const peopleMacro = macros.find((m) => m.name === 'People');
    const peoplePerms = roleRequestBody?.permissions?.find(
      (p) => p.resource_id === peopleMacro?.id,
    );

    let value = peoplePerms?.value;
    if (Array.isArray(value)) {
      value = value.reduce((acc, curr) => ({ ...acc, ...curr }), {});
    }

    const toggles = {
      'Manage Teams': value?.['Manage Teams'] ?? true,
      'Manage Users': value?.['Manage Users'] ?? true,
      'Manage Roles & Permissions':
        value?.['Manage Roles & Permissions'] ?? true,
    };
    return toggles;
  };

  const [peopleToggles, setPeopleToggles] = useState(getInitialPeopleToggles);
  useEffect(() => {
    setPeopleToggles(getInitialPeopleToggles());
  }, [roleRequestBody]);

  const [dialog, setDialog] = useState({ open: false, type: null });
  const [dontShowEnable, setDontShowEnable] = useState(false);
  const [dontShowDisable, setDontShowDisable] = useState(false);
  const [pendingToggles, setPendingToggles] = useState(null);
  // Decide which dialog key to use based on type
  const getDialogKey = (type) => {
    return type === 'enable'
      ? PEOPLE_DIALOG_KEY_ENABLE
      : PEOPLE_DIALOG_KEY_DISABLE;
  };

  const applyPeopleToggles = (newToggles) => {
    setPeopleToggles(newToggles);

    const peopleMacro = macros.find((m) => m.name === 'People');
    updateEditPermissions([
      {
        resource_id: peopleMacro?.id,
        parent: true,
        value: [newToggles],
      },
    ]);
    updateRoleRequestBody({
      resource_id: peopleMacro?.id,
      value: togglesObj([newToggles]),
    });
  };

  const handlePeopleTogglesChange = (newToggles, type) => {
    const dialogKey = getDialogKey(type);
    if (shouldSkipDialog(dialogKey) || !type) {
      applyPeopleToggles(newToggles);
    } else {
      setPendingToggles(newToggles);
      setDialog({ open: true, type });
    }
  };

  const handleDialogContinue = () => {
    setDialog({ open: false, type: null });
    if (pendingToggles) {
      applyPeopleToggles(pendingToggles);
      setPendingToggles(null);
    }
    // Use the correct dialog key and checkbox state
    if (dialog.type === 'enable' && dontShowEnable) {
      setSkipDialog(PEOPLE_DIALOG_KEY_ENABLE);
    }
    if (dialog.type === 'disable' && dontShowDisable) {
      setSkipDialog(PEOPLE_DIALOG_KEY_DISABLE);
    }
  };

  const handleDialogCancel = () => {
    setDialog({ open: false, type: null });
    setPendingToggles(null);
  };

  // Separate handlers for each checkbox
  const handleDontShowEnableChange = (e) => {
    setDontShowEnable(e.target.checked);
  };
  const handleDontShowDisableChange = (e) => {
    setDontShowDisable(e.target.checked);
  };

  return macros.map((macro) => {
    const { type, toggles, parent } = macro;
    if (HIDDEN_ROLES.includes(macro.id)) return null;
    let options = null;

    const onCrudFormChange = (value) => {
      updateEditPermissions([{ resource_id: macro.id, parent: true, value }]);
      updateRoleRequestBody({ resource_id: macro.id, value });
    };

    const onToggleFormChange = (value) => {
      updateEditPermissions([{ resource_id: macro.id, parent: true, value }]);
      updateRoleRequestBody({
        resource_id: macro.id,
        value: togglesObj(value),
      });
    };

    const onPermissionChange = (bool) => {
      if (macro.type === 'crud') {
        updateEditPermissions([
          { resource_id: macro.id, parent: bool, value: [0, 0, 0] },
        ]);
        updateRoleRequestBody({ resource_id: macro.id, value: [0, 0, 0] });
      } else if (macro.type === 'toggle') {
        const macroToggles = macro.toggles.map((t) => {
          const [toggle] = Object.keys(t);
          return { [toggle]: false };
        });
        updateEditPermissions([
          { resource_id: macro.id, parent: bool, value: macroToggles },
        ]);
        updateRoleRequestBody({
          resource_id: macro.id,
          value: togglesObj(macroToggles),
        });
      }
    };

    const onSinglePermissionToggle = (toggle) => {
      const [value] = Object.values(toggle);
      updateEditPermissions([
        { resource_id: macro.id, parent: value, value: [toggle] },
      ]);
      updateRoleRequestBody({ resource_id: macro.id, value: toggle });
    };

    const getParentPermissionHandler = (() => {
      if (macro.type === 'toggle' && macro.toggles.length === 1) {
        return onSinglePermissionToggle;
      }
      return onPermissionChange;
    })();

    if (macro.name === 'People') {
      options = (
        <PeoplePermissionsSection
          toggles={peopleToggles}
          onTogglesChange={handlePeopleTogglesChange}
          dialog={dialog}
          onDialogCancel={handleDialogCancel}
          onDialogContinue={handleDialogContinue}
          dontShow={dialog.type === 'enable' ? dontShowEnable : dontShowDisable}
          onDontShowChange={
            dialog.type === 'enable'
              ? handleDontShowEnableChange
              : handleDontShowDisableChange
          }
        />
      );
    } else {
      switch (type) {
        case 'crud':
          options = (
            <CrudForm
              onCrudFormChange={onCrudFormChange}
              parent={parent}
              toggles={toggles}
            />
          );
          break;
        case 'toggle':
          options = (
            <ToggleForm
              onToggleFormChange={onToggleFormChange}
              parent={parent}
              toggles={toggles}
            />
          );
          break;
        default:
          break;
      }
    }

    return (
      <Permission
        macro={macro}
        onPermissionChange={getParentPermissionHandler}
        key={macro.id}
        roleRequestBody={roleRequestBody}
      >
        {options}
      </Permission>
    );
  });
};
export default Form;
